import React from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { useDashboardStore } from '../../store/dashboardStore'
import { useAuth } from '../../contexts/AuthContext'
import ErrorBoundary from '../ui/ErrorBoundary'
import { buildApiUrl } from '../../config/api'

// Import view components
import { FileUploadComponent } from '../FileUpload'
import { SimpleAnalytics } from '../SimpleAnalytics'
import { SimpleAIInsights } from '../SimpleAIInsights'
import { EnhancedAnalyticsDashboard } from '../EnhancedAnalyticsDashboard'
import { DataUpdateDropdown } from '../DataUpdateDropdown'

const DashboardLayout: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { user, logout } = useAuth()
    const {
        uploadResult,
        uploadError,
        setUploadResult,
        setUploadError,
        resetErrors
    } = useDashboardStore()

    const handleUploadSuccess = (result: any) => {
        setUploadResult(result)
        setUploadError(null)
        console.log('Upload successful:', result)

        // Show success message and refresh analytics
        setTimeout(() => {
            // Force refresh of analytics components by clearing cache
            window.dispatchEvent(new CustomEvent('refreshAnalytics'))
        }, 500)
    }

    const handleUploadError = (error: string) => {
        setUploadError(error)
        setUploadResult(null)
        console.error('Upload error:', error)
    }

    const menuItems = [
        {
            id: 'overview',
            label: 'Overview',
            icon: '🏠',
            path: '/',
            description: 'Dashboard overview and key metrics'
        },
        {
            id: 'upload',
            label: 'Upload Data',
            icon: '📁',
            path: '/upload',
            description: 'Upload and process Excel files'
        },
        {
            id: 'analytics',
            label: 'Analytics',
            icon: '📊',
            path: '/analytics',
            description: 'Voucher performance and metrics'
        },
        {
            id: 'charts',
            label: 'Visualizations',
            icon: '📈',
            path: '/charts',
            description: 'Interactive charts and graphs'
        },
        {
            id: 'insights',
            label: 'AI Insights',
            icon: '🤖',
            path: '/insights',
            description: 'AI-powered analysis and recommendations'
        }
    ]

    const getCurrentMenuItem = () => {
        return menuItems.find(item => item.path === location.pathname) || menuItems[0]
    }

    return (
        <ErrorBoundary>
            <div className="h-screen bg-gray-50 flex overflow-hidden">
                {/* Sidebar Navigation */}
                <aside className="w-64 bg-white shadow-lg flex-shrink-0 relative">
                    <div className="p-6 border-b border-gray-200">
                        <h1 className="text-xl font-bold text-gray-900">ACI Voucher Dashboard</h1>
                        <p className="text-sm text-gray-600 mt-1">Analytics & Insights</p>
                    </div>

                    <nav className="mt-6 px-3">
                        {menuItems.map((item) => {
                            const isActive = location.pathname === item.path
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => navigate(item.path)}
                                    className={`w-full flex items-center px-3 py-3 mb-1 text-left rounded-lg transition-colors duration-200 group ${isActive
                                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500'
                                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                    title={item.description}
                                >
                                    <span className="mr-3 text-lg">{item.icon}</span>
                                    <div className="flex-1">
                                        <div className="font-medium">{item.label}</div>
                                        <div className="text-xs text-gray-500 group-hover:text-gray-600">
                                            {item.description}
                                        </div>
                                    </div>
                                </button>
                            )
                        })}
                    </nav>

                    {/* User info and logout */}
                    <div className="absolute bottom-4 left-4 right-4 space-y-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-gray-600">System Online</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-white text-sm font-medium">
                                            {user?.username?.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{user?.username}</div>
                                        <div className="text-xs text-gray-500">Authenticated</div>
                                    </div>
                                </div>
                                <button
                                    onClick={logout}
                                    className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-white transition-colors"
                                    title="Sign out"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                    {/* Header */}
                    <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
                        <div className="px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-semibold text-gray-900">
                                        {getCurrentMenuItem().label}
                                    </h2>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {getCurrentMenuItem().description}
                                    </p>
                                </div>

                                {/* Header actions */}
                                <div className="flex items-center space-x-3">
                                    <DataUpdateDropdown />
                                    {(uploadError || uploadResult) && (
                                        <button
                                            onClick={resetErrors}
                                            className="text-sm text-gray-500 hover:text-gray-700"
                                        >
                                            Clear notifications
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Main Content */}
                    <main className="flex-1 overflow-auto">
                        <div className="p-6 w-full">
                            <Routes>
                                <Route path="/" element={<OverviewView navigate={navigate} />} />
                                <Route path="/upload" element={
                                    <UploadView
                                        onUploadSuccess={handleUploadSuccess}
                                        onUploadError={handleUploadError}
                                        uploadResult={uploadResult}
                                        uploadError={uploadError}
                                    />
                                } />
                                <Route path="/analytics" element={<AnalyticsView />} />
                                <Route path="/charts" element={<ChartsView />} />
                                <Route path="/insights" element={<InsightsView />} />
                            </Routes>
                        </div>
                    </main>
                </div>
            </div>
        </ErrorBoundary>
    )
}

// View Components
const OverviewView: React.FC<{ navigate: (path: string) => void }> = ({ navigate }) => {
    const { uploadResult, analytics, filters } = useDashboardStore()
    const [liveAnalytics, setLiveAnalytics] = React.useState<any>(null)

    React.useEffect(() => {
        // Fetch fresh analytics data for overview
        const fetchOverviewData = async () => {
            try {
                // Build params to keep Overview consistent with other tabs
                const params = new URLSearchParams()
                if (filters?.quarter) params.set('quarter', filters.quarter as any)
                if ((filters as any)?.datasetTag) params.set('datasetTag', (filters as any).datasetTag as string)
                params.set('ts', String(Date.now()))
                const url = buildApiUrl(`/api/analytics/simple?${params.toString()}`)
                const response = await fetch(url, { method: 'GET', cache: 'no-store' })
                const result = await response.json()
                if (result.success) {
                    setLiveAnalytics(result)
                }
            } catch (error) {
                console.error('Error fetching overview analytics:', error)
            }
        }

        fetchOverviewData()

        // Listen for refresh events
        const handleRefresh = () => {
            fetchOverviewData()
        }

        window.addEventListener('refreshAnalytics', handleRefresh)

        return () => {
            window.removeEventListener('refreshAnalytics', handleRefresh)
        }
        // Re-fetch when global filters change
    }, [filters?.quarter, (filters as any)?.datasetTag])

    // Use live analytics if available, fallback to store analytics
    const displayAnalytics = liveAnalytics || analytics

    return (
        <div className="space-y-6">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                <h3 className="text-xl font-semibold mb-2">Welcome to ACI Voucher Dashboard</h3>
                <p className="text-blue-100">
                    Monitor voucher utilization, track exam performance, and gain insights into your certification program.
                </p>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <span className="text-2xl">📊</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Vouchers</p>
                            <p className="text-2xl font-semibold text-gray-900">
                                {displayAnalytics?.totalVouchers || uploadResult?.recordsProcessed || 0}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <span className="text-2xl">✅</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Redeemed</p>
                            <p className="text-2xl font-semibold text-gray-900">
                                {displayAnalytics?.redeemedCount || 0}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                            <span className="text-2xl">⏳</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Assigned</p>
                            <p className="text-2xl font-semibold text-gray-900">
                                {displayAnalytics?.assignedCount || 0}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <span className="text-2xl">🎯</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Pass Rate</p>
                            <p className="text-2xl font-semibold text-gray-900">
                                {displayAnalytics?.passRate !== undefined && displayAnalytics?.passRate !== null
                                    ? `${displayAnalytics.passRate.toFixed(1)}%`
                                    : displayAnalytics?.totalExams > 0
                                        ? '0.0%'
                                        : 'No Exams'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Program Summary */}
                                {displayAnalytics?.voucherValues && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Program Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="text-blue-700 font-medium">CLF (Foundational)</div>
                            <div className="text-2xl font-bold text-blue-900">
                                {displayAnalytics.voucherValues.foundationalCount}
                            </div>
                            <div className="text-blue-600">
                                ${displayAnalytics.voucherValues.foundationalValue?.toLocaleString()}
                            </div>
                            <div className="text-blue-500 text-sm">$75 each</div>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <div className="text-green-700 font-medium">DVA (Associate)</div>
                            <div className="text-2xl font-bold text-green-900">
                                {displayAnalytics.voucherValues.associateCount}
                            </div>
                            <div className="text-green-600">
                                ${displayAnalytics.voucherValues.associateValue?.toLocaleString()}
                            </div>
                            <div className="text-green-500 text-sm">$150 each</div>
                        </div>

                                            {(displayAnalytics.voucherValues as any).aifCount > 0 && (
                                                <div className="bg-purple-50 p-4 rounded-lg">
                                                    <div className="text-purple-700 font-medium">AIF</div>
                                                    <div className="text-2xl font-bold text-purple-900">
                                                        {(displayAnalytics.voucherValues as any).aifCount}
                                                    </div>
                                                    <div className="text-purple-600">
                                                        ${((displayAnalytics.voucherValues as any).aifValue)?.toLocaleString()}
                                                    </div>
                                                    <div className="text-purple-500 text-sm">$75 each</div>
                                                </div>
                                            )}

                                            <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-gray-700 font-medium">Total Program Value</div>
                            <div className="text-2xl font-bold text-gray-900">
                                ${displayAnalytics.voucherValues.totalValue?.toLocaleString()}
                            </div>
                            <div className="text-gray-600">
                                                    {displayAnalytics.totalVouchers} total vouchers
                            </div>
                            <div className="text-gray-500 text-sm">
                                {displayAnalytics.redeemedCount} redeemed ({((displayAnalytics.redeemedCount / displayAnalytics.totalVouchers) * 100).toFixed(1)}%)
                                {displayAnalytics.totalExams > 0 && (
                                    <div className="mt-1">
                                        {displayAnalytics.passCount} passed, {displayAnalytics.failCount} failed
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={() => navigate('/upload')}
                        className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <span className="text-2xl mr-3">📁</span>
                        <div className="text-left">
                            <div className="font-medium">Upload Data</div>
                            <div className="text-sm text-gray-600">Process new Excel files</div>
                        </div>
                    </button>

                    <button
                        onClick={() => navigate('/analytics')}
                        className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <span className="text-2xl mr-3">📊</span>
                        <div className="text-left">
                            <div className="font-medium">View Analytics</div>
                            <div className="text-sm text-gray-600">Explore performance metrics</div>
                        </div>
                    </button>

                    <button
                        onClick={() => navigate('/insights')}
                        className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <span className="text-2xl mr-3">🤖</span>
                        <div className="text-left">
                            <div className="font-medium">AI Insights</div>
                            <div className="text-sm text-gray-600">Get AI recommendations</div>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    )
}

const UploadView: React.FC<{
    onUploadSuccess: (result: any) => void
    onUploadError: (error: string) => void
    uploadResult: any
    uploadError: string | null
}> = ({ onUploadSuccess, onUploadError, uploadResult, uploadError }) => {
    const navigate = useNavigate()
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
                <FileUploadComponent
                    onUploadSuccess={onUploadSuccess}
                    onUploadError={onUploadError}
                />
            </div>

            <div className="space-y-4">
                {/* Upload Stats */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="font-semibold mb-3 flex items-center">
                        <span className="mr-2">📈</span>
                        Upload Statistics
                    </h3>
                    {uploadResult ? (
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Records Processed:</span>
                                <span className="font-medium">{uploadResult.recordsProcessed}</span>
                            </div>
                            {uploadResult.lastUpdateDate && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Last Updated:</span>
                                    <span className="font-medium">
                                        {new Date(uploadResult.lastUpdateDate).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                            <div className="pt-2 border-t">
                                <div className="flex items-center text-green-600">
                                    <span className="mr-1">✅</span>
                                    <span className="text-sm">Upload successful</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <span className="text-4xl mb-2 block">📁</span>
                            <p className="text-gray-500 text-sm">Upload a file to see statistics</p>
                        </div>
                    )}
                </div>

                {/* Error Display */}
                {uploadError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="text-red-800 font-medium mb-2 flex items-center">
                            <span className="mr-2">❌</span>
                            Upload Error
                        </h4>
                        <p className="text-red-700 text-sm">{uploadError}</p>
                    </div>
                )}

                {/* Help Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-blue-800 font-medium mb-2 flex items-center">
                        <span className="mr-2">💡</span>
                        Upload Tips
                    </h4>
                    <ul className="text-blue-700 text-sm space-y-1">
                        <li>• Supported formats: .xlsx, .xls</li>
                        <li>• Maximum file size: 10MB</li>
                        <li>• Ensure proper column headers</li>
                        <li>• Check for data validation errors</li>
                    </ul>
                </div>

                {/* Quick Navigation */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h4 className="font-semibold mb-3 flex items-center">
                        <span className="mr-2">🧭</span>
                        Quick Navigation
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            onClick={() => navigate('/')}
                            className="px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200 text-gray-800"
                        >
                            Overview
                        </button>
                        <button
                            onClick={() => navigate('/analytics')}
                            className="px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200 text-gray-800"
                        >
                            Analytics
                        </button>
                        <button
                            onClick={() => navigate('/charts')}
                            className="px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200 text-gray-800"
                        >
                            Visualizations
                        </button>
                        <button
                            onClick={() => navigate('/insights')}
                            className="px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200 text-gray-800"
                        >
                            AI Insights
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

const AnalyticsView: React.FC = () => {
    return (
        <div className="space-y-6">
            <SimpleAnalytics />
        </div>
    )
}

const ChartsView: React.FC = () => {
    return (
        <div className="space-y-6">
            <EnhancedAnalyticsDashboard />
        </div>
    )
}

const InsightsView: React.FC = () => {
    return (
        <div className="space-y-6">
            <SimpleAIInsights />
        </div>
    )
}

export default DashboardLayout